/**
 * RiskDetailPage - View detailed risk information with actions, timeline, triage
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate, formatDateTime } from '../lib/api';
import { queueActionStatusUpdate } from '../lib/offlineQueue';
import { SEVERITY_CONFIG } from '../lib/constants';
import { 
  Card, StatusBadge, SeverityBadge, TypeBadge, ActionStatusBadge,
  Spinner, EmptyState, Modal, Select, Input, Textarea, CustomButton as Button 
} from '../components/shared';

// ============== ACTION CARD ==============
const ActionCard = ({ action, onUpdate, onOptimisticUpdate, users }) => {
  const [updating, setUpdating] = useState(false);
  const isOverdue = action.status !== 'VERIFIED' && action.status !== 'DONE' && action.status !== 'CANCELLED' && 
    new Date(action.due_date) < new Date();

  /**
   * Handle status change with offline support
   * - Online: Normal API call
   * - Offline: Queue write + optimistic UI update
   */
  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    
    try {
      if (!navigator.onLine) {
        // OFFLINE: Queue the write and update UI optimistically
        await queueActionStatusUpdate(action.id, newStatus);
        
        // Optimistic update with _pending flag
        onOptimisticUpdate(action.id, { 
          status: newStatus, 
          _pending: true,
          _pendingStatus: newStatus,
          _queuedAt: new Date().toISOString()
        });
        
        console.log(`[Offline] Queued: Mark action ${action.id.slice(0,8)} as ${newStatus}`);
        return;
      }
      
      // ONLINE: Normal API call
      await api.patch(`/actions/${action.id}`, { status: newStatus });
      onUpdate();
    } catch (err) {
      console.error('Failed to update action:', err);
      // Revert optimistic update on error
      if (!navigator.onLine) {
        onUpdate(); // Refresh to get actual state
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleVerify = async () => {
    // Verify is NOT supported offline (requires RISK_TEAM+ permission check)
    if (!navigator.onLine) {
      alert('Verification requires online connection.');
      return;
    }
    
    setUpdating(true);
    try {
      await api.post(`/actions/${action.id}/verify`, { verified_by: 'demo-user', notes: 'Verified' });
      onUpdate();
    } catch (err) {
      console.error('Failed to verify action:', err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Card className={`action-card ${isOverdue ? 'overdue' : ''} ${action._pending ? 'pending' : ''}`}>
      <div className="action-header">
        <ActionStatusBadge status={action.status} />
        <span className={`action-priority priority-${action.priority}`}>{action.priority}</span>
        {isOverdue && <span className="overdue-badge">⏰ Overdue</span>}
        {action._pending && (
          <span className="pending-badge" data-testid="pending-badge">⏳ Pending sync</span>
        )}
      </div>
      <h4>{action.title}</h4>
      {action.description && <p>{action.description}</p>}
      <div className="action-meta">
        <span>Assigned to: {action.assigned_to_name}</span>
        <span>Due: {formatDate(action.due_date)}</span>
      </div>
      <div className="action-buttons">
        {action.status === 'OPEN' && (
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={() => handleStatusChange('IN_PROGRESS')} 
            disabled={updating || action._pending}
            data-testid={`start-action-${action.id}`}
          >
            Start
          </Button>
        )}
        {action.status === 'IN_PROGRESS' && (
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={() => handleStatusChange('DONE')} 
            disabled={updating || action._pending}
            data-testid={`mark-done-${action.id}`}
          >
            {!navigator.onLine ? '📴 Mark Done (Offline)' : 'Mark Done'}
          </Button>
        )}
        {action.status === 'DONE' && action.verification_required && !action._pending && (
          <Button 
            size="sm" 
            variant="success" 
            onClick={handleVerify} 
            disabled={updating || !navigator.onLine}
            title={!navigator.onLine ? 'Verification requires online connection' : ''}
            data-testid={`verify-action-${action.id}`}
          >
            Verify
          </Button>
        )}
      </div>
      {action.verified_at && (
        <div className="action-verified">
          ✅ Verified {formatDateTime(action.verified_at)}
        </div>
      )}
      {action._pending && (
        <div className="action-pending-note">
          This change will sync when you're back online.
        </div>
      )}
    </Card>
  );
};

// ============== TRIAGE MODAL ==============
const TriageModal = ({ isOpen, onClose, risk, users, onSave }) => {
  const [formData, setFormData] = useState({
    severity: 'MEDIUM',
    escalation: 'NONE',
    risk_owner_id: '',
    triage_notes: '',
    inherent_likelihood: 3,
    inherent_consequence: 3
  });
  const [saving, setSaving] = useState(false);

  const calculateRating = (l, c) => {
    const score = l * c;
    if (score >= 20) return 'CRITICAL';
    if (score >= 12) return 'HIGH';
    if (score >= 6) return 'MEDIUM';
    return 'LOW';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const owner = users.find(u => u.id === formData.risk_owner_id);
      await api.patch(`/risks/${risk.id}`, {
        ...formData,
        risk_owner_name: owner?.display_name,
        inherent_rating: calculateRating(formData.inherent_likelihood, formData.inherent_consequence)
      });
      await api.post(`/risks/${risk.id}/transition`, { status: 'TRIAGED', triage_notes: formData.triage_notes });
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to triage:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Triage Report" size="lg">
      <div className="triage-form">
        <div className="form-row">
          <Select
            label="Severity *"
            value={formData.severity}
            onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
            options={Object.entries(SEVERITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
          />
          <Select
            label="Escalation"
            value={formData.escalation}
            onChange={(e) => setFormData(prev => ({ ...prev, escalation: e.target.value }))}
            options={[
              { value: 'NONE', label: 'None' },
              { value: 'SUPERVISOR', label: 'Supervisor' },
              { value: 'EXEC', label: 'Executive' },
              { value: 'EMERGENCY', label: 'Emergency' },
            ]}
          />
        </div>

        <Select
          label="Assign Owner *"
          value={formData.risk_owner_id}
          onChange={(e) => setFormData(prev => ({ ...prev, risk_owner_id: e.target.value }))}
          options={[
            { value: '', label: 'Select owner...' },
            ...users.map(u => ({ value: u.id, label: u.display_name }))
          ]}
        />

        <div className="risk-matrix-section">
          <h4>Risk Assessment</h4>
          <div className="form-row">
            <div className="input-group">
              <label>Likelihood (1-5)</label>
              <input
                type="range"
                min="1"
                max="5"
                value={formData.inherent_likelihood}
                onChange={(e) => setFormData(prev => ({ ...prev, inherent_likelihood: parseInt(e.target.value) }))}
              />
              <span className="range-value">{formData.inherent_likelihood}</span>
            </div>
            <div className="input-group">
              <label>Consequence (1-5)</label>
              <input
                type="range"
                min="1"
                max="5"
                value={formData.inherent_consequence}
                onChange={(e) => setFormData(prev => ({ ...prev, inherent_consequence: parseInt(e.target.value) }))}
              />
              <span className="range-value">{formData.inherent_consequence}</span>
            </div>
          </div>
          <div className="rating-preview">
            Rating: <SeverityBadge severity={calculateRating(formData.inherent_likelihood, formData.inherent_consequence)} />
          </div>
        </div>

        <Textarea
          label="Triage Notes"
          value={formData.triage_notes}
          onChange={(e) => setFormData(prev => ({ ...prev, triage_notes: e.target.value }))}
          placeholder="Initial assessment and any immediate actions taken..."
          rows={3}
        />

        <div className="modal-actions">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={!formData.risk_owner_id}>Complete Triage</Button>
        </div>
      </div>
    </Modal>
  );
};

// ============== ADD ACTION MODAL ==============
const AddActionModal = ({ isOpen, onClose, riskId, users, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'P3',
    assigned_to: '',
    due_date: '',
    verification_required: true
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/actions', { ...formData, risk_id: riskId });
      onSave();
      onClose();
      setFormData({ title: '', description: '', priority: 'P3', assigned_to: '', due_date: '', verification_required: true });
    } catch (err) {
      console.error('Failed to create action:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Action" size="md">
      <div className="action-form">
        <Input
          label="Title *"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="What needs to be done?"
        />

        <Textarea
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Additional details..."
          rows={2}
        />

        <div className="form-row">
          <Select
            label="Priority *"
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
            options={[
              { value: 'P1', label: 'P1 - Critical' },
              { value: 'P2', label: 'P2 - High' },
              { value: 'P3', label: 'P3 - Medium' },
              { value: 'P4', label: 'P4 - Low' },
            ]}
          />
          <Input
            label="Due Date *"
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
          />
        </div>

        <Select
          label="Assign To *"
          value={formData.assigned_to}
          onChange={(e) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
          options={[
            { value: '', label: 'Select assignee...' },
            ...users.map(u => ({ value: u.id, label: u.display_name }))
          ]}
        />

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.verification_required}
            onChange={(e) => setFormData(prev => ({ ...prev, verification_required: e.target.checked }))}
          />
          <span className="checkbox-text">Requires verification before closing</span>
        </label>

        <div className="modal-actions">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            variant="primary" 
            onClick={handleSave} 
            loading={saving}
            disabled={!formData.title || !formData.assigned_to || !formData.due_date}
          >
            Create Action
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ============== MAIN COMPONENT ==============
const RiskDetailPage = ({ riskId, setCurrentPage, currentRole }) => {
  const [risk, setRisk] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTriageModal, setShowTriageModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
  }, [riskId]);

  // Listen for sync events to clear pending flags
  useEffect(() => {
    const handleMessage = (event) => {
      const { type, client_request_id, action: actionDesc } = event.data || {};
      
      if (type === 'WRITE_SUCCESS' || type === 'WRITE_CONFLICT') {
        // Clear pending flag for this action
        setActions(prev => prev.map(a => {
          if (a._pending && actionDesc?.includes(a.id.slice(0, 8))) {
            return { ...a, _pending: false };
          }
          return a;
        }));
        
        // Refresh data to get server state
        loadData();
      }
      
      if (type === 'QUEUE_FLUSHED') {
        // Refresh after queue flush to sync UI
        loadData();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [riskRes, actionsRes, usersRes] = await Promise.all([
        api.get(`/risks/${riskId}`),
        api.get(`/actions?risk_id=${riskId}`),
        api.get('/users')
      ]);
      setRisk(riskRes);
      // Preserve pending flags on reload
      setActions(prev => {
        const pendingIds = new Set(prev.filter(a => a._pending).map(a => a.id));
        return actionsRes.items.map(a => ({
          ...a,
          _pending: pendingIds.has(a.id) ? true : false
        }));
      });
      setUsers(usersRes.items);
    } catch (err) {
      console.error('Failed to load risk:', err);
    } finally {
      setLoading(false);
    }
  };

  // Optimistic update for offline actions
  const handleOptimisticUpdate = (actionId, updates) => {
    setActions(prev => prev.map(a => 
      a.id === actionId ? { ...a, ...updates } : a
    ));
  };

  const handleTransition = async (newStatus, notes = '') => {
    try {
      await api.post(`/risks/${riskId}/transition`, { status: newStatus, triage_notes: notes, closure_notes: notes });
      loadData();
    } catch (err) {
      console.error('Failed to transition:', err);
    }
  };

  if (loading) return <div className="page-loading"><Spinner /></div>;
  if (!risk) return <EmptyState icon="❌" title="Risk not found" description="The requested risk could not be found" />;

  const canTriage = ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'].includes(currentRole) && risk.status === 'NEW';
  const canClose = ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'].includes(currentRole) && risk.status === 'VERIFICATION';

  return (
    <div className="page risk-detail-page" data-testid="risk-detail-page">
      <div className="page-header">
        <Button variant="ghost" onClick={() => setCurrentPage('risks')}>← Back to Reports</Button>
      </div>

      {/* Risk Header */}
      <Card className="risk-header-card">
        <div className="risk-header-top">
          <div className="risk-header-left">
            <TypeBadge type={risk.report_type} />
            <h1>{risk.title}</h1>
            <div className="risk-meta">
              <span>ID: {risk.id.slice(0, 8)}</span>
              <span>•</span>
              <span>{risk.site_name}</span>
              <span>•</span>
              <span>Reported {formatDateTime(risk.created_at)}</span>
              {risk.created_by_name && <span>by {risk.created_by_name}</span>}
            </div>
          </div>
          <div className="risk-header-right">
            <div className="risk-badges">
              {risk.restricted && <span className="badge badge-restricted">🔒 Restricted</span>}
              {risk.immediate_danger && <span className="badge badge-danger">🚨 Immediate Danger</span>}
            </div>
            <StatusBadge status={risk.status} size="lg" />
            {risk.severity && <SeverityBadge severity={risk.severity} />}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="risk-actions">
          {canTriage && (
            <Button variant="primary" onClick={() => setShowTriageModal(true)}>Triage Report</Button>
          )}
          {risk.status === 'TRIAGED' && ['SUPERVISOR', 'RISK_TEAM'].includes(currentRole) && (
            <Button variant="secondary" onClick={() => handleTransition('IN_TREATMENT')}>Start Treatment</Button>
          )}
          {risk.status === 'IN_TREATMENT' && (
            <Button variant="secondary" onClick={() => handleTransition('VERIFICATION')}>Request Verification</Button>
          )}
          {canClose && (
            <Button variant="success" onClick={() => handleTransition('CLOSED')}>Close Report</Button>
          )}
          <Button variant="ghost" onClick={() => setShowActionModal(true)}>+ Add Action</Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="detail-tabs">
        <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`tab ${activeTab === 'actions' ? 'active' : ''}`} onClick={() => setActiveTab('actions')}>Actions ({actions.length})</button>
        <button className={`tab ${activeTab === 'timeline' ? 'active' : ''}`} onClick={() => setActiveTab('timeline')}>Timeline</button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="detail-grid">
            <Card>
              <h3>Description</h3>
              <p>{risk.description || 'No description provided'}</p>
              {risk.location_details && (
                <>
                  <h4>Location</h4>
                  <p>{risk.location_details}</p>
                </>
              )}
            </Card>

            <Card>
              <h3>Assessment</h3>
              {risk.inherent_likelihood && risk.inherent_consequence ? (
                <div className="assessment-grid">
                  <div className="assessment-item">
                    <span className="assessment-label">Likelihood</span>
                    <span className="assessment-value">{risk.inherent_likelihood}/5</span>
                  </div>
                  <div className="assessment-item">
                    <span className="assessment-label">Consequence</span>
                    <span className="assessment-value">{risk.inherent_consequence}/5</span>
                  </div>
                  <div className="assessment-item">
                    <span className="assessment-label">Rating</span>
                    <SeverityBadge severity={risk.inherent_rating} />
                  </div>
                </div>
              ) : (
                <p className="no-data">Not yet assessed</p>
              )}
            </Card>

            <Card>
              <h3>Assignment</h3>
              <div className="assignment-info">
                <div className="assignment-row">
                  <span className="assignment-label">Owner</span>
                  <span>{risk.risk_owner_name || 'Unassigned'}</span>
                </div>
                {risk.triage_notes && (
                  <div className="assignment-row">
                    <span className="assignment-label">Triage Notes</span>
                    <span>{risk.triage_notes}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'actions' && (
        <div className="tab-content">
          {actions.length === 0 ? (
            <EmptyState 
              icon="✅" 
              title="No actions yet" 
              description="Create actions to address this risk"
              action={<Button variant="primary" onClick={() => setShowActionModal(true)}>+ Add Action</Button>}
            />
          ) : (
            <div className="actions-list">
              {actions.map(action => (
                <ActionCard 
                  key={action.id} 
                  action={action} 
                  onUpdate={loadData} 
                  onOptimisticUpdate={handleOptimisticUpdate}
                  users={users} 
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="tab-content">
          <Card>
            <div className="timeline">
              <div className="timeline-item">
                <span className="timeline-dot"></span>
                <div className="timeline-content">
                  <span className="timeline-time">{formatDateTime(risk.created_at)}</span>
                  <span className="timeline-text">Report created by {risk.created_by_name}</span>
                </div>
              </div>
              {risk.triage_completed_at && (
                <div className="timeline-item">
                  <span className="timeline-dot"></span>
                  <div className="timeline-content">
                    <span className="timeline-time">{formatDateTime(risk.triage_completed_at)}</span>
                    <span className="timeline-text">Triaged - {risk.triage_notes}</span>
                  </div>
                </div>
              )}
              {risk.closed_at && (
                <div className="timeline-item">
                  <span className="timeline-dot success"></span>
                  <div className="timeline-content">
                    <span className="timeline-time">{formatDateTime(risk.closed_at)}</span>
                    <span className="timeline-text">Report closed - {risk.closure_notes}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Triage Modal */}
      <TriageModal 
        isOpen={showTriageModal} 
        onClose={() => setShowTriageModal(false)} 
        risk={risk}
        users={users}
        onSave={loadData}
      />

      {/* Add Action Modal */}
      <AddActionModal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        riskId={riskId}
        users={users}
        onSave={loadData}
      />
    </div>
  );
};

export default RiskDetailPage;
